import cn from 'classnames'
import Avatar from 'ext-ui/components/Avatar/Avatar'
import Button from 'ext-ui/components/Button/Button'
import LabelToggle, {
  LabelToggleOption,
} from 'ext-ui/components/LabelToggle/LabelToggle'
import Pill from 'ext-ui/components/Pill/Pill'
import RoundCard from 'ext-ui/components/RoundCard/RoundCard'
import { base_colorAction } from 'ext-ui/style/colors'
import AiStarsIcon from 'ext-ui/svg/icon/AiStarsIcon'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import React, { ReactElement, useMemo, useState } from 'react'
import PricingEnterprisePNG from 'src/assets/images/pricing-enterprise.png'
import PricingProPNG from 'src/assets/images/pricing-pro.png'
import PricingStarterPNG from 'src/assets/images/pricing-starter.png'
import { CurrencyFormatter } from 'src/localization/currencyFormatter'
import { TranslateFn } from 'src/localization/translate'
import { PricingPlan } from 'src/queries/organizations/pricingPlans/pricingPlans.models'

import {
  buildEntitlementDisplayName,
  buildSeatCapDisplayName,
} from './pricingPlanSeatCap'

export type PlanPeriod = 'MONTHLY' | 'YEARLY'

export interface PricingTierCardCtaAction {
  href?: string
  loading?: boolean
  onClick?: () => Promise<void> | void
  rel?: string
  target?: string
}

const AVATAR_MAP: Record<string, string> = {
  starter: PricingStarterPNG,
  pro: PricingProPNG,
  enterprise: PricingEnterprisePNG,
}

function getPlanAvatar(planId: string) {
  if (planId.includes('starter')) {
    return AVATAR_MAP.starter
  } else if (planId.includes('pro')) {
    return AVATAR_MAP.pro
  } else if (planId.includes('enterprise')) {
    return AVATAR_MAP.enterprise
  }
  return AVATAR_MAP.starter
}

function formatCentsToPrice(
  cents: number,
  planPeriod: PlanPeriod,
): { dollars: string; cents: string } {
  if (planPeriod === 'YEARLY') {
    // For yearly plans, we want to show the monthly equivalent price, so we divide by 12
    cents = cents / 12
  }
  const total = cents / 100
  const dollarPart = Math.floor(total)
  const centPart = Math.round((total - dollarPart) * 100)
  return {
    dollars: `$${dollarPart}`,
    cents: centPart > 0 ? `.${centPart.toString().padStart(2, '0')}` : '',
  }
}

function buildPriceElement(
  plan: PricingPlan,
  planPeriod: PlanPeriod,
  translate: (key: string, params?: Record<string, any>) => string,
): ReactElement {
  const interval =
    planPeriod === 'YEARLY'
      ? plan.pricing.billingIntervals.yearly
      : plan.pricing.billingIntervals.monthly

  const amountCents = interval?.amountCentsPerUser

  if (amountCents === 0) {
    return <h1 className="PricingCard-per-bigPrice">{'$0'}</h1>
  }

  if (plan.pricing.model === 'flat_fee' || amountCents === undefined) {
    return <h1 className="PricingCard-per-bigPrice">{translate('Custom')}</h1>
  }

  const { dollars, cents } = formatCentsToPrice(amountCents, planPeriod)
  return (
    <h1 className="PricingCard-per-bigPrice">
      {dollars}
      {cents && <span className="PricingCard-per-superScript">{cents}</span>}
    </h1>
  )
}

function buildStrikeOutPrice(
  plan: PricingPlan,
  planPeriod: PlanPeriod,
): number | undefined {
  if (planPeriod !== 'YEARLY' || plan.pricing.model === 'flat_fee') {
    return undefined
  }

  const monthlyAmountCents =
    plan.pricing.billingIntervals.monthly?.amountCentsPerUser
  const yearlyAmountCents =
    plan.pricing.billingIntervals.yearly?.amountCentsPerUser

  if (!monthlyAmountCents || !yearlyAmountCents) return undefined

  return monthlyAmountCents / 100
}

function buildPriceSubtext(
  plan: PricingPlan,
  planPeriod: PlanPeriod,
  translate: (key: string, params?: Record<string, any>) => string,
): string | undefined {
  const interval =
    planPeriod === 'YEARLY'
      ? plan.pricing.billingIntervals.yearly
      : plan.pricing.billingIntervals.monthly

  const amountCents = interval?.amountCentsPerUser

  if (amountCents === 0) {
    return translate('Free')
  }

  if (plan.pricing.model === 'flat_fee') {
    return translate('Contact us for pricing')
  }

  if (planPeriod === 'YEARLY') {
    return translate('*Billed annually')
  }

  return undefined
}

function buildPriceUnit(
  plan: PricingPlan,
  planPeriod: PlanPeriod,
  translate: (key: string, params?: Record<string, any>) => string,
): string | undefined {
  const interval =
    planPeriod === 'YEARLY'
      ? plan.pricing.billingIntervals.yearly
      : plan.pricing.billingIntervals.monthly

  const amountCents = interval?.amountCentsPerUser

  if (amountCents !== 0 && plan.pricing.model === 'flat_fee') {
    return undefined
  }

  if (amountCents === 0 || planPeriod === 'MONTHLY') {
    return translate('/user/month')
  }

  if (planPeriod === 'YEARLY') {
    return translate('/user/month*')
  }

  return undefined
}

function buildGetStartedHeading(
  planIndex: number,
  translate: (key: string, params?: Record<string, any>) => string,
  previousPlanName?: string,
): string {
  if (planIndex === 0) {
    return translate('Get started with:')
  }

  if (previousPlanName) {
    return translate('Everything in ${previousPlanName}, plus:', {
      previousPlanName: previousPlanName,
    })
  }

  return translate('Get started with:')
}

function buildTierFeatures(
  plan: PricingPlan,
): { icon: ReactElement; text: string }[] {
  const tierFeatures = plan.entitlements.map((entitlement) => ({
    icon: entitlement.aiPowered ? (
      <AiStarsIcon />
    ) : (
      <CheckmarkIcon fill={base_colorAction} />
    ),
    text: buildEntitlementDisplayName(plan, entitlement),
  }))

  return tierFeatures
}

export interface DynamicPricingTierCardProps {
  plan: PricingPlan
  planIndex: number
  previousPlanName?: string
  isNonExtendIssuer?: boolean
  currencyFormatter: CurrencyFormatter
  translate: TranslateFn
  getPlanCtaAction: (args: {
    plan: PricingPlan
    planPeriod: PlanPeriod
  }) => PricingTierCardCtaAction
}

const DynamicPricingTierCard = (
  props: DynamicPricingTierCardProps,
): React.ReactElement => {
  const {
    plan,
    planIndex,
    previousPlanName,
    isNonExtendIssuer,
    currencyFormatter,
    translate,
    getPlanCtaAction,
  } = props

  const [planPeriod, setPlanPeriod] = useState<PlanPeriod>('YEARLY')

  const badge = useMemo(() => {
    if (planPeriod === 'MONTHLY') return plan.monthlyBadge
    if (planPeriod === 'YEARLY') return plan.yearlyBadge
    return undefined
  }, [planPeriod])
  const variant = badge ? 'highlight' : undefined
  const shiny = isNonExtendIssuer && variant === 'highlight'

  const showBillingToggle =
    plan.pricing.billingIntervals.monthly !== null &&
    plan.pricing.billingIntervals.yearly !== null &&
    plan.pricing.billingIntervals.monthly.amountCentsPerUser !==
      plan.pricing.billingIntervals.yearly.amountCentsPerUser

  const pricingCardAvatar = getPlanAvatar(plan.id)
  const priceElement = buildPriceElement(plan, planPeriod, translate)
  const strikeOutPrice = buildStrikeOutPrice(plan, planPeriod)
  const priceSubtext = buildPriceSubtext(plan, planPeriod, translate)
  const getStartedHeading = buildGetStartedHeading(
    planIndex,
    translate,
    previousPlanName,
  )
  const tierFeatures = buildTierFeatures(plan)
  const seatCapDisplayName = buildSeatCapDisplayName(plan)
  const perUserText = buildPriceUnit(plan, planPeriod, translate)

  const [showAllFeatures, setShowAllFeatures] = useState(false)

  const displayedFeatures = showAllFeatures
    ? tierFeatures
    : tierFeatures.slice(0, plan.numKeyFeatures)
  const ctaAction = getPlanCtaAction({ plan, planPeriod })

  const handlePlanPeriodChange = (selectedValue: LabelToggleOption): void => {
    setPlanPeriod(selectedValue.value as PlanPeriod)
  }

  const ctaButton = (
    <Button
      variant={variant === 'highlight' ? 'primary' : 'secondary'}
      className={'w-full'}
      loading={ctaAction.loading}
      onClick={ctaAction.onClick}
    >
      {showBillingToggle && planPeriod === 'YEARLY'
        ? translate('Continue with Pro')
        : plan.ctaText}
    </Button>
  )

  return (
    <RoundCard
      className={cn('PricingCard-component flex-column gap-6', {
        'PricingCard-component-pro': variant === 'highlight',
      })}
      style={
        {
          '--column-display-order': plan.columnDisplayOrder,
        } as React.CSSProperties
      }
      variant={variant}
    >
      {badge && (
        <Pill
          className="PricingCard-pro-promoText"
          color={shiny ? 'shiny' : 'saas'}
        >
          {badge}
        </Pill>
      )}
      <div className="flex-column align-start gap-3 w-full">
        <div className="flex-row w-full justify-between">
          <Avatar
            className="PricingCard-Avatar PricingCard-Avatar-enterprise"
            imageSrc={pricingCardAvatar}
          />
          {showBillingToggle && (
            <LabelToggle
              className="PricingCard-billing-toggle"
              data-testid={`pricing-tier-period-toggle-${plan.displayName}`}
              options={[
                {
                  displayName: translate('Monthly'),
                  value: 'MONTHLY',
                },
                {
                  displayName: translate('Yearly'),
                  value: 'YEARLY',
                },
              ]}
              name={`planPeriod_${plan.displayName}`}
              onChange={handlePlanPeriodChange}
              value={planPeriod}
            />
          )}
        </div>
        <div className="flex-column">
          <p className="font-h3">{plan.displayName}</p>
          <p className="PricingCard-name-topText">
            {plan.pricingTableDescription}
          </p>
        </div>
        <div className="flex-row align-center gap-3">
          {strikeOutPrice && (
            <div className="PricingCard-strikeout">
              {currencyFormatter({ amount: strikeOutPrice })}
            </div>
          )}
          <div className={'flex-row align-baseline'}>
            <p className={cn('PricingCard-price', { shiny })}>{priceElement}</p>
            {perUserText && (
              <p className="font-regular text-secondary">{perUserText}</p>
            )}
          </div>
        </div>
        <div className="PricingCard-price-details gap-1">
          <p className="PricingCard-price-details-text">{seatCapDisplayName}</p>
          {priceSubtext && (
            <p className="PricingCard-price-details-text">{priceSubtext}</p>
          )}
        </div>
      </div>
      {ctaAction.href ? (
        <a
          href={ctaAction.href}
          rel={ctaAction.rel}
          target={ctaAction.target}
          data-testid={`pricing-tier-button-${plan.displayName}`}
          className={'w-full'}
        >
          {ctaButton}
        </a>
      ) : (
        <div
          data-testid={`pricing-tier-button-${plan.displayName}`}
          className={'w-full'}
        >
          {ctaButton}
        </div>
      )}
      <div className="flex-column gap-6 w-full">
        {getStartedHeading && (
          <p className="PricingCard-getStarted-heading">{getStartedHeading}</p>
        )}
        {plan.aiPowered && (
          <div className="PricingCard-AI-Powered flex-row align-center gap-1">
            <div className="flex-column align-center">
              <AiStarsIcon />
            </div>
            <div className="PricingCard-AI-Powered-text">
              {translate('AI POWERED')}
            </div>
          </div>
        )}
        <div className="flex-column gap-2">
          {displayedFeatures.map((feature, i) => (
            <div className="flex-row align-center gap-1" key={i}>
              <div>{feature.icon}</div>
              <div className="font-regular text-secondary">{feature.text}</div>
            </div>
          ))}
        </div>
        {plan.numKeyFeatures < tierFeatures.length && (
          <Button
            testId={'viewAllFeaturesToggle'}
            variant={'no-shell-no-padding'}
            dropShadow={false}
            className={'justify-start'}
            onClick={(): void => {
              setShowAllFeatures((prev) => !prev)
            }}
          >
            {showAllFeatures
              ? translate('View less')
              : translate('View all features')}
          </Button>
        )}
      </div>
    </RoundCard>
  )
}

export default DynamicPricingTierCard
